import { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useTransform, useReducedMotion } from 'framer-motion';

/**
 * Background video — auto-loops a muted MP4 behind its children, with smart
 * progressive enhancement:
 *
 * 1. **Reduced motion** (prefers-reduced-motion: reduce) → static gradient
 *    + poster image if provided. Unless `force={true}` (brand-critical use).
 * 2. **Slow connection** (connection.saveData or 2G/slow-2g) → static gradient,
 *    video never loaded.
 * 3. **Mobile / narrow viewport** (< 768px) → static gradient by default
 *    unless `mobileVideo` is true.
 * 4. **Off-screen** → video stays paused; resumes when scrolled into view.
 * 5. **Tab hidden** → video pauses to save battery.
 *
 * 2026-05-16 : Jade signale ne pas voir la vidéo du home hero. Cause
 * probable : reduced-motion enabled sur son iPhone (parfois par
 * défaut). Ajout du `force` prop pour le bypass (use case home hero =
 * asset de marque). Le poster image se montre toujours quand video
 * skipped → quoi qu'il arrive, le hero n'est pas un gradient vide.
 *
 * 2026-06-20 : `parallax` (opt-in) — la couche fond (gradient/poster/vidéo)
 * défile plus lentement que la page pour un effet de profondeur premium. Le
 * média est sur-dimensionné (scale 1.22) DANS le wrapper overflow-hidden, donc
 * la translation ne révèle jamais de bord. Coupé sous reduced-motion. Compatible
 * Lenis (framer useScroll tracke via le pont d'events de <SmoothScroll/>).
 *
 * The video is positioned absolutely behind `children`, with a configurable
 * gradient overlay on top to keep text readable.
 *
 * Files are stored locally in /public/videos/ to avoid CDN drift / API key
 * surprises. Originals sourced from Pexels (free for commercial use).
 *
 * @param {object} props
 * @param {string} props.src               Path to .mp4 (e.g. "/videos/panama-skyline.mp4")
 * @param {string} [props.poster]          Optional fallback image (always shown until video plays + if video skipped)
 * @param {React.ReactNode} props.children Foreground content
 * @param {string} [props.className]       Wrapper extra classes
 * @param {string} [props.overlay]         Tailwind classes for the dark overlay (default = bordeaux gradient)
 * @param {boolean} [props.mobileVideo]    Force-load video on mobile (default false)
 * @param {boolean} [props.force]          Bypass reduce-motion check (for brand-critical hero videos). Default false.
 * @param {boolean} [props.parallax]       Subtle scroll parallax on the background layer. Default false.
 *
 * @example
 *   <BgVideo src="/videos/panama-skyline.mp4" className="rounded-card overflow-hidden">
 *     <h1 className="text-cream-50">Welcome to Panama</h1>
 *   </BgVideo>
 */
export default function BgVideo({
  src,
  poster,
  children,
  className = '',
  overlay = 'bg-gradient-to-b from-navy-900/60 via-navy-900/40 to-navy-900/70',
  mobileVideo = false,
  force = false,
  parallax = false,
}) {
  const videoRef = useRef(null);
  const wrapperRef = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const reduce = useReducedMotion();

  // Parallaxe du fond : la couche média translate plus lentement que le scroll.
  const { scrollYProgress } = useScroll({ target: wrapperRef, offset: ['start start', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], ['-7%', '9%']);
  const parallaxOn = parallax && !reduce;

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // 1. Respect reduced-motion preference (unless force=true for brand-critical use)
    if (!force) {
      const reducedMotion =
        window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
      if (reducedMotion) return;
    }

    // 2. Skip on slow connections / save-data (always honored, even if force=true)
    const connection =
      navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (connection?.saveData) return;
    if (connection?.effectiveType && /^(slow-2g|2g)$/.test(connection.effectiveType)) return;

    // 3. Skip on narrow viewports unless explicitly enabled
    const isMobile = window.innerWidth < 768;
    if (isMobile && !mobileVideo) return;

    // 4. Lazy load when wrapper is near viewport
    const el = wrapperRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShouldLoad(true);
            observer.disconnect();
          }
        });
      },
      { rootMargin: '200px' }, // start loading slightly before it enters viewport
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [mobileVideo, force]);

  // Pause when offscreen / tab hidden — avoids burning bandwidth+battery
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !shouldLoad) return;

    const onVisibility = () => {
      if (document.hidden) v.pause();
      else v.play().catch(() => {/* autoplay may be blocked */});
    };
    document.addEventListener('visibilitychange', onVisibility);

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) v.play().catch(() => {});
        else v.pause();
      },
      { threshold: 0.1 },
    );
    obs.observe(v);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      obs.disconnect();
    };
  }, [shouldLoad]);

  return (
    <div ref={wrapperRef} className={`relative isolate overflow-hidden ${className}`}>
      {/* Couche fond (gradient + poster + vidéo) — translatée en parallaxe quand
          `parallax` est actif. Sur-dimensionnée (scale 1.22) pour que la
          translation ne révèle jamais de bord dans le wrapper overflow-hidden. */}
      <motion.div
        aria-hidden="true"
        className="absolute inset-0 -z-20"
        style={parallaxOn ? { y: bgY } : undefined}
      >
        <div className={`absolute inset-0 ${parallaxOn ? 'scale-[1.22]' : ''}`}>
          {/* Fallback gradient — couvre le gap jusqu'à/sauf si la vidéo charge. */}
          <div className="absolute inset-0 bg-gradient-to-br from-navy-900 via-bordeaux-800 to-teal-900" />

          {/* Poster — toujours rendu si fourni (cas reduce-motion / 2G). */}
          {poster && (
            <img
              src={poster}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 h-full w-full object-cover"
            />
          )}

          {/* Video — montée après le gate lazy-load, couvre le poster (cross-fade). */}
          {shouldLoad && (
            <video
              ref={videoRef}
              src={src}
              poster={poster}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              aria-hidden="true"
              className="absolute inset-0 h-full w-full object-cover"
            />
          )}
        </div>
      </motion.div>

      {/* Overlay tint — keeps foreground text legible */}
      <div aria-hidden="true" className={`absolute inset-0 -z-10 ${overlay}`} />

      {/* Foreground content */}
      {children}
    </div>
  );
}
